/**
 * The account's state on the device and what it allows (part 36a, table AC, principles C1, C2, C8). Pure: the
 * controller passes the record, the copy it verified and the time, and gets the state, whether the writer may change
 * their books, and what the interface says of the trial.
 */
import type { Email } from './email.ts';
import { copyWrites, daysLeft } from './trial.ts';
import type { TrialCopy } from './trial.ts';
import type { AccountId } from './wire.ts';

/**
 * AC1–AC8: `off`: the build names no relay (no gate, writing as before). `loading`: the record isn't read yet. `none`:
 * no account; `gate` until the writer says "not now". `account`: logged in, or signed out by the relay (`signedOut`,
 * C8), with the newest copy that verified (null for none) and the purge date last told.
 */
export type AccountStatus =
  | { readonly kind: 'off' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'none'; readonly gate: boolean }
  | {
      readonly kind: 'account';
      readonly id: AccountId;
      readonly email: Email;
      readonly signedOut: boolean;
      readonly copy: TrialCopy | null;
      readonly purgeAt: number | null;
      /** Part 38a: when an account that began as a guest loses its own shares and links. */
      readonly sharesEndAt: number | null;
    };

/** C1, C2, AC1–AC7: whether the writer may change their books at `at` (the time C2 judges at). */
export function mayWrite(status: AccountStatus, at: number): boolean {
  switch (status.kind) {
    case 'off':
      return true;
    case 'loading':
    case 'none':
      return false;
    case 'account':
      return status.copy !== null && copyWrites(status.copy, at);
  }
}

/** Part 38c (decision 75): whether the account is a guest that never started a trial (its copy: no trial end, not paid). */
export function isGuest(status: AccountStatus): boolean {
  return status.kind === 'account' && status.copy !== null && status.copy.trialEndsAt === null && !status.copy.paid;
}

/** What the interface says of the account's trial (the header's chip, the banner, settings). */
export type TrialStanding =
  /** No account, or accounts off: nothing to say of a trial (the banner says why it is read-only, if it is). */
  | { readonly kind: 'none' }
  /** Bought: writing for good (decision 23: "the purchase shown as unlocked"). */
  | { readonly kind: 'paid' }
  /** In the trial: whole days left, rounded up. */
  | { readonly kind: 'trial'; readonly daysLeft: number }
  /**
   * The trial ended unpaid: read-only, with the purge date last told (null: not told yet), or for an account that began
   * as a guest the date its own shares and links end (part 38a: it isn't purged).
   */
  | { readonly kind: 'ended'; readonly purgeAt: number | null; readonly sharesEndAt: number | null }
  /** No copy that verifies yet (AC6): read-only until one does. */
  | { readonly kind: 'unknown' };

/** The trial's standing for the interface at `at`. */
export function trialStanding(status: AccountStatus, at: number): TrialStanding {
  if (status.kind !== 'account') return { kind: 'none' };
  const copy = status.copy;
  if (copy === null) return { kind: 'unknown' };
  if (copy.paid) return { kind: 'paid' };
  const days = daysLeft(copy, at);
  return days === null || days === 0 ? { kind: 'ended', purgeAt: status.purgeAt, sharesEndAt: status.sharesEndAt } : { kind: 'trial', daysLeft: days };
}
